import React, { useState, useEffect } from "react";
import { Routes, Route, NavLink } from "react-router-dom";
import axios from "axios";
import { FaBuilding, FaPlus, FaGlobe, FaCity, FaList, FaBook, FaTags } from "react-icons/fa";

const API_BASE = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000";
const withCreds = { withCredentials: true };

const fieldLabels = {
  name: "Name",
  countryId: "Country",
  cityId: "City",
  checkInTime: "Check-in Time",
  checkOutTime: "Check-out Time",
  description: "Description",
  maxOccupancy: "Max Occupancy (People per room)",
  price: "Price per room (₹)",
};

export default function AdminHotelPage() {
  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className="w-56 bg-white border-r border-gray-200 shadow-sm shrink-0">
        <div className="p-4 border-b border-gray-100">
          <h2 className="font-bold text-gray-800 flex items-center gap-2">
            <FaBuilding className="text-blue-600" /> Hotel Admin
          </h2>
        </div>
        <nav className="p-3 space-y-0.5">
          {[
            { to: "/admin/hotel", end: true, label: "Overview", icon: FaList },
            { to: "/admin/hotel/countries", label: "Countries", icon: FaGlobe },
            { to: "/admin/hotel/cities", label: "Cities", icon: FaCity },
            { to: "/admin/hotel/hotels", label: "Hotels", icon: FaBuilding },
            { to: "/admin/hotel/categories", label: "Categories", icon: FaTags },
            { to: "/admin/hotel/bookings", label: "Bookings", icon: FaBook },
          ].map(({ to, end, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={!!end}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-100"}`
              }
            >
              {Icon && <Icon className="opacity-80" />} {label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <main className="flex-1 p-6 overflow-auto">
        <Routes>
          <Route index element={<AdminHotelOverview />} />
          <Route path="countries" element={
            <AdminHotelSection
              key="countries"
              name="Countries"
              entityKey="countries"
              fields={["name"]}
              tableColumns={[{ label: "Name", get: (i) => i.name }]}
            />
          } />
          <Route path="cities" element={
            <AdminHotelSection
              key="cities"
              name="Cities"
              entityKey="cities"
              fields={["countryId", "name"]}
              optionsApis={{ countryId: { entityKey: "countries", labelKey: "name" } }}
              tableColumns={[
                { label: "City Name", get: (i) => i.name },
                { label: "Country", get: (i) => i.countryId?.name || "—" }
              ]}
            />
          } />
          <Route path="hotels" element={
            <AdminHotelSection
              key="hotels"
              name="Hotels"
              entityKey="hotels"
              fields={["cityId", "name", "checkInTime", "checkOutTime", "description"]}
              optionsApis={{ cityId: { entityKey: "cities", labelKey: "name" } }}
              tableColumns={[
                { label: "Hotel Name", get: (i) => i.name },
                { label: "City", get: (i) => i.cityId?.name || "—" }
              ]}
            />
          } />
          <Route path="categories" element={
            <AdminHotelSection
              key="categories"
              name="Categories"
              entityKey="categories"
              fields={["hotelId", "name", "price", "maxOccupancy"]}
              optionsApis={{ hotelId: { entityKey: "hotels", labelKey: "name" } }}
              tableColumns={[
                { label: "Category Name", get: (i) => i.name },
                { label: "Price", get: (i) => `₹${i.price || 0}` },
                { label: "Max Occ.", get: (i) => `${i.maxOccupancy || 2} pax` },
                { label: "Hotel", get: (i) => i.hotelId?.name || "—" }
              ]}
            />
          } />
          <Route path="bookings" element={<AdminHotelBookings />} />
        </Routes>
      </main>
    </div>
  );
}

function AdminHotelOverview() {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    const apis = ["/api/hotel/admin/countries", "/api/hotel/admin/cities", "/api/hotel/admin/hotels", "/api/hotel/admin/bookings"];
    Promise.all(apis.map((u) => axios.get(API_BASE + u, withCreds).then((r) => r.data?.data?.length ?? 0).catch(() => 0)))
      .then(([a, b, c, d]) => setCounts({ countries: a, cities: b, hotels: c, bookings: d }));
  }, []);
  const cards = [
    { label: "Countries", value: counts.countries ?? "—" },
    { label: "Cities", value: counts.cities ?? "—" },
    { label: "Hotels", value: counts.hotels ?? "—" },
    { label: "Bookings", value: counts.bookings ?? "—" },
  ];
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Hotel Module Overview</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map(({ label, value }) => (
          <div key={label} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <p className="text-gray-500 text-sm font-medium">{label}</p>
            <p className="text-2xl font-bold mt-1 text-gray-900">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminHotelSection({ name, entityKey, fields, optionsApis = {}, tableColumns }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(null);

  // Image handling
  const [existingImages, setExistingImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  // For multiple category creation
  const [categoriesData, setCategoriesData] = useState([{ name: "", price: "", maxOccupancy: "", files: [], previewUrls: [], existingImages: [], bookingDateRangeFrom: "", bookingDateRangeTo: "", advanceBookingDays: "" }]);

  const optionKeys = Object.keys(optionsApis);
  const [optionsMap, setOptionsMap] = useState({});

  useEffect(() => {
    const fetchAll = async () => {
      const next = {};
      for (const key of optionKeys) {
        const entity = optionsApis[key]?.entityKey;
        if (!entity) continue;
        try {
          const r = await axios.get(`${API_BASE}/api/hotel/admin/${entity}`, withCreds);
          next[key] = r.data?.data || [];
        } catch {
          next[key] = [];
        }
      }
      setOptionsMap(next);
    };
    fetchAll();
  }, [entityKey]);

  const fetchList = () => {
    setLoading(true);
    axios.get(`${API_BASE}/api/hotel/admin/${entityKey}`, withCreds)
      .then((r) => setList(r.data?.data || []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  };
  useEffect(fetchList, [entityKey]);

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Multiple categories handling
    if (entityKey === "categories") {
      try {
        const hotelId = form.hotelId;
        const validCats = categoriesData.map(c => ({
          name: c.name.trim(),
          price: Number(c.price),
          maxOccupancy: Number(c.maxOccupancy) || 2,
          files: c.files || [],
          existingImages: c.existingImages || [],
          remark: c.remark || "",
          breakfast: c.breakfast || "Not Included",
          extraBedCharges: c.extraBedCharges || "Not Included",
          bookingDateRangeFrom: c.bookingDateRangeFrom || null,
          bookingDateRangeTo: c.bookingDateRangeTo || null,
          advanceBookingDays: c.advanceBookingDays !== "" ? Number(c.advanceBookingDays) : null
        })).filter(c => c.name.length > 0 && c.price > 0);

        let createdIds = [];
        if (editingId) {
          const promises = [];
          if (validCats.length > 0) {
            promises.push(
              axios.put(`${API_BASE}/api/hotel/admin/${entityKey}/${editingId}`, { hotelId, name: validCats[0].name, price: validCats[0].price, maxOccupancy: validCats[0].maxOccupancy, images: validCats[0].existingImages, remark: validCats[0].remark, breakfast: validCats[0].breakfast, extraBedCharges: validCats[0].extraBedCharges, bookingDateRangeFrom: validCats[0].bookingDateRangeFrom, bookingDateRangeTo: validCats[0].bookingDateRangeTo, advanceBookingDays: validCats[0].advanceBookingDays }, withCreds)
                .then(r => r.data?.data?._id || editingId)
            );
          }
          for (let i = 1; i < validCats.length; i++) {
            promises.push(
              axios.post(`${API_BASE}/api/hotel/admin/${entityKey}`, { hotelId, name: validCats[i].name, price: validCats[i].price, maxOccupancy: validCats[i].maxOccupancy, remark: validCats[i].remark, breakfast: validCats[i].breakfast, extraBedCharges: validCats[i].extraBedCharges, bookingDateRangeFrom: validCats[i].bookingDateRangeFrom, bookingDateRangeTo: validCats[i].bookingDateRangeTo, advanceBookingDays: validCats[i].advanceBookingDays }, withCreds)
                .then(r => r.data?.data?._id)
            );
          }
          createdIds = (await Promise.all(promises)).filter(Boolean);
        } else {
          const promises = validCats.map(cat =>
            axios.post(`${API_BASE}/api/hotel/admin/${entityKey}`, { hotelId, name: cat.name, price: cat.price, maxOccupancy: cat.maxOccupancy, remark: cat.remark, breakfast: cat.breakfast, extraBedCharges: cat.extraBedCharges, bookingDateRangeFrom: cat.bookingDateRangeFrom, bookingDateRangeTo: cat.bookingDateRangeTo, advanceBookingDays: cat.advanceBookingDays }, withCreds)
              .then(r => r.data?.data?._id)
          );
          createdIds = (await Promise.all(promises)).filter(Boolean);
        }

        const uploadPromises = [];
        createdIds.forEach((docId, index) => {
          if (validCats[index] && validCats[index].files?.length > 0) {
            const formData = new FormData();
            Array.from(validCats[index].files).forEach(file => formData.append("images", file));
            uploadPromises.push(
              axios.post(`${API_BASE}/api/hotel/admin/categories/${docId}/images`, formData, { ...withCreds, headers: { "Content-Type": "multipart/form-data" } })
            );
          }
        });

        if (uploadPromises.length > 0) {
          await Promise.all(uploadPromises).catch(err => {
            console.error("Image upload failed for categories", err);
            alert("Categories saved, but image upload failed.");
          });
        }

        setShowForm(false); setForm({}); setCategoriesData([{ name: "", price: "", maxOccupancy: "", remark: "", breakfast: "Not Included", extraBedCharges: "Not Included", files: [], previewUrls: [], existingImages: [], bookingDateRangeFrom: "", bookingDateRangeTo: "", advanceBookingDays: "" }]); setEditingId(null); setSelectedFiles(null); setExistingImages([]); setPreviewUrls([]); fetchList();
      } catch (err) {
        alert(err.response?.data?.error || "Failed to save categories");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const payload = fields.reduce((acc, f) => {
      const v = form[f];
      if (v === "" || v == null) return acc;
      return { ...acc, [f]: v };
    }, {});

    if (entityKey === "hotels" && editingId) {
      payload.images = existingImages;
    }

    const request = editingId
      ? axios.put(`${API_BASE}/api/hotel/admin/${entityKey}/${editingId}`, payload, withCreds)
      : axios.post(`${API_BASE}/api/hotel/admin/${entityKey}`, payload, withCreds);

    request
      .then(async (res) => {
        const docId = editingId || res.data?.data?._id;
        if (entityKey === "hotels" && selectedFiles?.length > 0 && docId) {
          const formData = new FormData();
          Array.from(selectedFiles).forEach(file => formData.append("images", file));
          try {
            await axios.post(`${API_BASE}/api/hotel/admin/hotels/${docId}/images`, formData, {
              ...withCreds, headers: { "Content-Type": "multipart/form-data" }
            });
          } catch (err) {
            console.error("Image upload failed", err);
            alert("Hotel saved, but image upload failed.");
          }
        }
        setShowForm(false); setEditingId(null); setForm({}); setSelectedFiles(null); setCategoriesData([{ name: "", price: "" }]);
        setExistingImages([]); setPreviewUrls([]);
        fetchList();
      })
      .catch((err) => alert(err.response?.data?.error || "Failed"))
      .finally(() => setSubmitting(false));
  };

  const handleEdit = (item) => {
    const editForm = {};
    fields.forEach(f => {
      editForm[f] = (item[f] && typeof item[f] === 'object') ? item[f]._id : item[f];
    });
    setForm(editForm);
    if (entityKey === "categories") setCategoriesData([{ name: item.name || "", price: item.price || "", maxOccupancy: item.maxOccupancy || "", remark: item.remark || "", breakfast: item.breakfast || "Not Included", extraBedCharges: item.extraBedCharges || "Not Included", files: [], previewUrls: [], existingImages: item.images || [], bookingDateRangeFrom: item.bookingDateRangeFrom ? item.bookingDateRangeFrom.split('T')[0] : "", bookingDateRangeTo: item.bookingDateRangeTo ? item.bookingDateRangeTo.split('T')[0] : "", advanceBookingDays: item.advanceBookingDays || "" }]);
    if (entityKey === "hotels") setExistingImages(item.images || []);
    setEditingId(item._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to completely delete this item?")) return;
    try {
      await axios.delete(`${API_BASE}/api/hotel/admin/${entityKey}/${id}`, withCreds);
      fetchList();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete");
    }
  };

  const isNumberField = (f) => ["maxOccupancy", "price"].includes(f);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{name}</h1>
        {!showForm && (
          <button type="button" onClick={() => { setForm({}); setEditingId(null); setExistingImages([]); setPreviewUrls([]); setSelectedFiles(null); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm">
            <FaPlus /> Add
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleCreateOrUpdate} className="mb-6 p-5 bg-white rounded-xl shadow-sm border border-gray-100 space-y-4">
          <h3 className="font-semibold text-gray-800">{editingId ? "Edit item" : "Add new"}</h3>
          {entityKey === "hotels" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Images</label>

              {/* Image Previews */}
              <div className="flex flex-wrap gap-2 mb-3">
                {existingImages.map((img, idx) => {
                  const imgSrc = img.startsWith('http') ? img : (img.startsWith('/') ? `${API_BASE}${img}` : `${API_BASE}/${img}`);
                  return (
                    <div key={`exist-${idx}`} className="relative inline-block">
                      <img src={imgSrc} className="w-20 h-20 object-cover rounded-lg border border-gray-200" />
                      <button type="button" onClick={() => setExistingImages(existingImages.filter((_, i) => i !== idx))} className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs shadow-md">✕</button>
                    </div>
                  );
                })}
                {previewUrls.map((url, idx) => (
                  <div key={`new-${idx}`} className="relative inline-block">
                    <img src={url} className="w-20 h-20 object-cover rounded-lg border border-gray-200 opacity-80" />
                    <span className="absolute bottom-1 left-1 bg-black bg-opacity-60 text-white text-[10px] px-1.5 py-0.5 rounded">New</span>
                  </div>
                ))}
              </div>

              <input type="file" multiple accept="image/*" onChange={(e) => {
                const files = Array.from(e.target.files);
                setSelectedFiles(files);
                setPreviewUrls(files.map(f => URL.createObjectURL(f)));
              }} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              <p className="text-xs text-gray-500 mt-1">Select multiple images. Note: Uploading new images will add to existing ones.</p>
            </div>
          )}
          {fields.map((f) => {
            const label = fieldLabels[f] || f;
            const hasDropdown = optionsApis[f];
            const opts = hasDropdown ? (optionsMap[f] || []) : [];

            return (
              <div key={f}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                {hasDropdown ? (
                  <select
                    value={form[f] || ""}
                    onChange={(e) => setForm((s) => ({ ...s, [f]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select {label}</option>
                    {opts.map((o) => (
                      <option key={o._id} value={o._id}>
                        {o[optionsApis[f].labelKey || "name"]}
                      </option>
                    ))}
                  </select>
                ) : (entityKey === "categories" && f === "name") ? (
                  <div className="space-y-4">
                    {categoriesData.map((cat, idx) => (
                      <div key={idx} className="flex flex-col gap-3 p-4 border border-gray-200 rounded-lg bg-gray-50/50">
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            type="text"
                            value={cat.name}
                            onChange={(e) => {
                              const newCats = [...categoriesData];
                              newCats[idx].name = e.target.value;
                              setCategoriesData(newCats);
                            }}
                            className="flex-[2] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder={editingId && idx === 0 ? "Edit category name" : `New Category Name`}
                            required={idx === 0}
                          />
                          <div className="flex-[1] flex items-center gap-2 w-full sm:w-auto">
                            <input
                              type="number"
                              value={cat.price}
                              onChange={(e) => {
                                const newCats = [...categoriesData];
                                newCats[idx].price = e.target.value;
                                setCategoriesData(newCats);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="Price (₹)"
                              required={idx === 0}
                              min="0"
                            />
                            <input
                              type="number"
                              value={cat.maxOccupancy}
                              onChange={(e) => {
                                const newCats = [...categoriesData];
                                newCats[idx].maxOccupancy = e.target.value;
                                setCategoriesData(newCats);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="Max Occ."
                              required={idx === 0}
                              min="1"
                            />
                            {categoriesData.length > 1 && (
                              <button type="button" onClick={() => setCategoriesData(categoriesData.filter((_, i) => i !== idx))} className="shrink-0 px-2 py-2 text-red-500 hover:bg-red-50 rounded-lg">✕</button>
                            )}
                          </div>
                        </div>
                        
                        {/* Custom Category Fields */}
                        <div className="flex flex-col sm:flex-row gap-2 mt-2">
                          <input
                            type="text"
                            value={cat.remark || ""}
                            onChange={(e) => {
                              const newCats = [...categoriesData];
                              newCats[idx].remark = e.target.value;
                              setCategoriesData(newCats);
                            }}
                            className="flex-[2] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                            placeholder="Remark (optional)"
                          />
                          <select
                            value={cat.breakfast || "Not Included"}
                            onChange={(e) => {
                              const newCats = [...categoriesData];
                              newCats[idx].breakfast = e.target.value;
                              setCategoriesData(newCats);
                            }}
                            className="flex-[1] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="Not Included">Breakfast: Not Included</option>
                            <option value="Included">Breakfast: Included</option>
                          </select>
                          {Number(cat.maxOccupancy) >= 3 && (
                            <select
                              value={cat.extraBedCharges || "Not Included"}
                              onChange={(e) => {
                                const newCats = [...categoriesData];
                                newCats[idx].extraBedCharges = e.target.value;
                                setCategoriesData(newCats);
                              }}
                              className="flex-[1] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              <option value="Not Included">Extra Bed: Not Included</option>
                              <option value="Included">Extra Bed: Included</option>
                            </select>
                          )}
                        </div>

                        {/* Booking Restrictions */}
                        <div className="flex flex-col gap-2 mt-2 bg-white p-3 border border-gray-100 rounded">
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Booking date range (optional)</label>
                          <div className="flex gap-2">
                            <input
                              type="date"
                              value={cat.bookingDateRangeFrom || ""}
                              onChange={(e) => {
                                const newCats = [...categoriesData];
                                newCats[idx].bookingDateRangeFrom = e.target.value;
                                setCategoriesData(newCats);
                              }}
                              className="flex-[1] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                            <input
                              type="date"
                              value={cat.bookingDateRangeTo || ""}
                              onChange={(e) => {
                                const newCats = [...categoriesData];
                                newCats[idx].bookingDateRangeTo = e.target.value;
                                setCategoriesData(newCats);
                              }}
                              className="flex-[1] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                            />
                          </div>
                          
                          <label className="block text-xs font-semibold text-gray-700 mb-1 mt-2">Advance Booking Days (optional)</label>
                          <input
                            type="number"
                            value={cat.advanceBookingDays || ""}
                            onChange={(e) => {
                              const newCats = [...categoriesData];
                              newCats[idx].advanceBookingDays = e.target.value;
                              setCategoriesData(newCats);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                            placeholder="e.g. 5"
                            min="0"
                          />
                        </div>

                        {/* Image upload per category */}
                        <div className="bg-white p-3 border border-gray-100 rounded">
                          <label className="block text-xs font-semibold text-gray-700 mb-2">Category Images</label>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {cat.existingImages?.map((img, imgIdx) => {
                              const imgSrc = img.startsWith('http') ? img : (img.startsWith('/') ? `${API_BASE}${img}` : `${API_BASE}/${img}`);
                              return (
                                <div key={`exist-${imgIdx}`} className="relative inline-block">
                                  <img src={imgSrc} className="w-16 h-16 object-cover rounded border border-gray-200" />
                                  <button type="button" onClick={() => {
                                    const newCats = [...categoriesData];
                                    newCats[idx].existingImages = newCats[idx].existingImages.filter((_, i) => i !== imgIdx);
                                    setCategoriesData(newCats);
                                  }} className="absolute -top-1.5 -right-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] shadow-md">✕</button>
                                </div>
                              );
                            })}
                            {cat.previewUrls?.map((url, urlIdx) => (
                              <div key={`new-${urlIdx}`} className="relative inline-block">
                                <img src={url} className="w-16 h-16 object-cover rounded border border-gray-200 opacity-80" />
                                <span className="absolute bottom-0.5 left-0.5 bg-black bg-opacity-60 text-white text-[9px] px-1 rounded">New</span>
                              </div>
                            ))}
                          </div>
                          <input type="file" multiple accept="image/*" onChange={(e) => {
                            const files = Array.from(e.target.files);
                            const newCats = [...categoriesData];
                            newCats[idx].files = files;
                            newCats[idx].previewUrls = files.map(f => URL.createObjectURL(f));
                            setCategoriesData(newCats);
                          }} className="w-full text-sm text-gray-500 file:mr-4 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                        </div>
                      </div>
                    ))}
                    <button type="button" onClick={() => setCategoriesData([...categoriesData, { name: "", price: "", maxOccupancy: "", files: [], previewUrls: [], existingImages: [], bookingDateRangeFrom: "", bookingDateRangeTo: "", advanceBookingDays: "" }])} className="text-sm text-blue-600 font-medium hover:underline">+ Add another category</button>
                  </div>
                ) : (entityKey === "categories" && (f === "price" || f === "maxOccupancy")) ? (
                  // Hide fields handled in the multi-category block above
                  <div className="hidden"></div>
                ) : (
                  <input
                    type={isNumberField(f) ? "number" : "text"}
                    step={isNumberField(f) ? "any" : undefined}
                    value={form[f] ?? ""}
                    onChange={(e) => setForm((s) => ({ ...s, [f]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder={label}
                    required={f !== "description"}
                  />
                )}
              </div>
            );
          })}
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={submitting} className="px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
              {editingId ? "Update" : "Save"}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setForm({}); setSelectedFiles(null); setCategoriesData([{ name: "", price: "", bookingDateRangeFrom: "", bookingDateRangeTo: "", advanceBookingDays: "" }]); setExistingImages([]); setPreviewUrls([]); }} className="px-4 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="min-w-full">
            {tableColumns?.length > 0 && (
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {tableColumns.map((col) => (
                    <th key={col.label} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{col.label}</th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
            )}
            <tbody>
              {list.map((item) => (
                <tr key={item._id} className="border-b border-gray-100 hover:bg-gray-50/50">
                  {tableColumns?.length > 0 ? tableColumns.map((col) => (
                    <td key={col.label} className="px-4 py-3 text-sm text-gray-800">{col.get ? col.get(item) : item[col.key]}</td>
                  )) : (
                    <td className="px-4 py-3 text-sm">{item.name || item._id}</td>
                  )}
                  <td className="px-4 py-3 text-right text-sm">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800 font-medium">Edit</button>
                      <button onClick={() => handleDelete(item._id)} className="text-red-500 hover:text-red-700 font-medium">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.length === 0 && <p className="p-4 text-gray-500 text-sm">No records found.</p>}
        </div>
      )}
    </div>
  );
}

function AdminHotelBookings() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBooking, setEditingBooking] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchBookings = () => {
    setLoading(true);
    axios.get(`${API_BASE}/api/hotel/admin/bookings`, withCreds)
      .then((r) => setList(r.data?.data || []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  };
  useEffect(fetchBookings, []);

  const handleUploadDoc = async (id, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("document", file);
    try {
      await axios.post(`${API_BASE}/api/hotel/admin/bookings/${id}/document`, formData, {
        ...withCreds,
        headers: { "Content-Type": "multipart/form-data" }
      });
      alert("Document uploaded successfully");
      fetchBookings();
    } catch (err) {
      alert("Failed to upload document");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API_BASE}/api/hotel/admin/bookings/${id}`, { status }, withCreds);
      fetchBookings();
    } catch (err) {
      alert("Failed to update status");
    }
  };

  const handleEditChange = (field, value) => {
    setEditingBooking(prev => ({ ...prev, [field]: value }));
  };

  const handlePassengerChange = (index, field, value) => {
    const updated = [...(editingBooking.passengers || [])];
    updated[index] = { ...updated[index], [field]: value };

    if (field === 'age' && value !== '') {
      const ageNum = Number(value);
      if (ageNum <= 4) updated[index].type = 'Child';
      else if (ageNum >= 60) updated[index].type = 'Senior';
      else updated[index].type = 'Adult';
    }

    setEditingBooking(prev => ({ ...prev, passengers: updated }));
  };

  const handleAddPassenger = () => {
    setEditingBooking(prev => ({
      ...prev,
      passengers: [...(prev.passengers || []), { name: '', age: '', gender: 'Male', mobileNumber: '', type: 'Adult' }]
    }));
  };

  const handleRemovePassenger = (index) => {
    setEditingBooking(prev => {
      const updated = [...(prev.passengers || [])];
      updated.splice(index, 1);
      return { ...prev, passengers: updated };
    });
  };

  const saveBookingEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Recalculate counts
    let adults = 0, children = 0, seniors = 0;
    (editingBooking.passengers || []).forEach(p => {
      if (p.type === 'Child') children++;
      else if (p.type === 'Senior') seniors++;
      else adults++;
    });

    const payload = {
      checkInDate: editingBooking.checkInDate,
      checkOutDate: editingBooking.checkOutDate,
      totalAmount: editingBooking.totalAmount,
      passengers: editingBooking.passengers,
      passengerCounts: { adults, children, seniors, total: adults + children + seniors },
      status: editingBooking.status
    };

    try {
      await axios.put(`${API_BASE}/api/hotel/admin/bookings/${editingBooking._id}`, payload, withCreds);
      setEditingBooking(null);
      fetchBookings();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to save edit");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Are you sure you want to delete this booking?")) return;
    try {
      await axios.delete(`${API_BASE}/api/hotel/admin/bookings/${id}`, withCreds);
      fetchBookings();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete booking");
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Hotel Bookings</h1>

      {editingBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center shrink-0">
              <h2 className="text-xl font-bold text-gray-800">Edit Booking - {editingBooking.hotelId?.name}</h2>
              <button onClick={() => setEditingBooking(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <form onSubmit={saveBookingEdit} className="p-6 overflow-y-auto flex-1 space-y-6">

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Date</label>
                  <input type="date" value={editingBooking.checkInDate?.split('T')[0] || ""} onChange={(e) => handleEditChange('checkInDate', e.target.value)} required className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Date</label>
                  <input type="date" value={editingBooking.checkOutDate?.split('T')[0] || ""} onChange={(e) => handleEditChange('checkOutDate', e.target.value)} required className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount (₹)</label>
                  <input type="number" value={editingBooking.totalAmount} onChange={(e) => handleEditChange('totalAmount', e.target.value)} required className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-800">Passengers</h3>
                  <button type="button" onClick={handleAddPassenger} className="text-sm bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg font-medium hover:bg-blue-100">+ Add Passenger</button>
                </div>

                <div className="space-y-4">
                  {(editingBooking.passengers || []).map((p, i) => (
                    <div key={i} className="flex flex-col md:flex-row gap-3 bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                        <input type="text" value={p.name} onChange={e => handlePassengerChange(i, 'name', e.target.value)} required className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                      </div>
                      <div className="w-20">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Age</label>
                        <input type="number" min="0" value={p.age} onChange={e => handlePassengerChange(i, 'age', e.target.value)} required className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                      </div>
                      <div className="w-28">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Gender</label>
                        <select value={p.gender} onChange={e => handlePassengerChange(i, 'gender', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
                          <option>Male</option><option>Female</option><option>Other</option>
                        </select>
                      </div>
                      <div className="w-32">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Mobile</label>
                        <input type="tel" value={p.mobileNumber || ''} onChange={e => handlePassengerChange(i, 'mobileNumber', e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                      </div>
                      <div className="w-24">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                        <div className="py-1 text-sm font-semibold">{p.type}</div>
                      </div>
                      <div className="flex items-end pb-1">
                        <button type="button" onClick={() => handleRemovePassenger(i)} className="text-red-500 hover:bg-red-50 p-1 rounded" title="Remove">✕</button>
                      </div>
                    </div>
                  ))}
                  {(!editingBooking.passengers || editingBooking.passengers.length === 0) && <p className="text-sm text-gray-500">No passengers.</p>}
                </div>
              </div>

            </form>
            <div className="p-4 border-t border-gray-100 flex justify-end gap-2 shrink-0">
              <button type="button" onClick={() => setEditingBooking(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={saveBookingEdit} disabled={submitting} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Hotel</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Dates</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((b) => (
                <tr key={b._id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">
                    <p className="font-semibold">{b.userId?.username}</p>
                    <p className="text-gray-500">{b.userId?.phone || b.userId?.email}</p>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">
                    {b.hotelId?.name}
                    {b.categoryId && (
                      <div className="mt-1">
                        <span className="block text-xs text-blue-600 font-semibold">{b.categoryId.name}</span>
                        {(b.categoryId.remark || b.categoryId.breakfast || b.categoryId.extraBedCharges) && (
                          <div className="mt-1 text-[10px] text-gray-500 space-y-0.5 leading-tight">
                            {b.categoryId.remark && <div><span className="font-semibold">Remark:</span> {b.categoryId.remark}</div>}
                            {b.categoryId.breakfast && <div><span className="font-semibold">Breakfast:</span> {b.categoryId.breakfast}</div>}
                            {b.categoryId.extraBedCharges && <div><span className="font-semibold">Extra Bed:</span> {b.categoryId.extraBedCharges}</div>}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {new Date(b.checkInDate).toLocaleDateString()} to {new Date(b.checkOutDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">₹{b.totalAmount}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${b.status === "confirmed" ? "bg-green-100 text-green-700" :
                      b.status === "cancelled" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"
                      }`}>
                      {b.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-sm">
                    <div className="flex justify-end gap-2 items-center">
                      <select
                        value={b.status}
                        onChange={(e) => updateStatus(b._id, e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirm</option>
                        <option value="cancelled">Cancel</option>
                      </select>
                      <button
                        onClick={() => setEditingBooking(JSON.parse(JSON.stringify(b)))}
                        className="text-blue-600 font-medium hover:underline text-sm ml-2"
                      >
                        Edit
                      </button>
                      <label className="text-purple-600 font-medium hover:underline text-sm ml-2 cursor-pointer">
                        Upload Doc
                        <input 
                          type="file" 
                          className="hidden" 
                          onChange={(e) => handleUploadDoc(b._id, e.target.files[0])}
                        />
                      </label>
                      {b.adminDocument && (
                        <a href={b.adminDocument.startsWith('http') ? b.adminDocument : `${API_BASE}/${b.adminDocument}`} target="_blank" rel="noopener noreferrer" className="text-green-600 font-medium hover:underline text-sm ml-2">Doc</a>
                      )}
                      <button
                        onClick={() => deleteBooking(b._id)}
                        className="text-red-600 font-medium hover:underline text-sm ml-2"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.length === 0 && <p className="p-4 text-gray-500 text-sm">No bookings found.</p>}
        </div>
      )}
    </div>
  );
}
